import { lazy } from "react";
import tennisPreview from "./simpleTennis/tennisPreview.png";
import LightOn from "./lightOn/LightOn";
import lightOnPreview from "./lightOn/light-on-preview.svg";
import prisonBreakPreview from "./prisonBreak/prisonBreakPreview.png";
import ticPreview from "./tictactoe/tic-preview.svg";
import guesserLogo from "./guesser/guesser-logo.svg";
import balloonGamesPreview from "./balloonGames/balloon-games.png";
import rpsPreview from "./rpsTournament/rps-preview.png";
import shipBattlePreview from "./shipBattle/ship-battle-preview.png";
import mazeMakerPreview from "./mazeMaker/maze-maker-preview.png";
import riskySpacePreview from "./riskySpace/risky-space-preview.svg";
import snakePreview from "./snake/snake-preview.svg";
import sirtetPreview from "./sirtet/sirtet-preview.svg";
import tankStikePreview from "./tankStike/tank-stike-preview.svg";

export interface GameDefinition {
  name: string;
  title: string;
  /** Date added to the site (YYYY-MM-DD), separate from the original project year. */
  createdAt: string;
  desc?: string;
  summary?: string;
  originalFramework?: string;
  originalLanguage?: string;
  originalYear?: string;
  image?: any;
  component?: any;
  author?: string;
  remakeFramework?: string;
  remakeLanguage?: string;
  remakeYear?: string;
  fullscreen?: boolean;
}

// Existing dates are backfilled from the first catalog entry in Git history.
export const gameDefinitions: GameDefinition[] = [
  {
    name: "guesser",
    summary: "Find the hidden number using the higher-or-lower clues.",
    createdAt: "2024-09-29",
    title: "Guess the number",
    desc: "A number guessing game originally written in Basic for the TI-86 Calculator in 2002",
    originalFramework: "TI-86 Calculator",
    originalLanguage: "Basic",
    image: guesserLogo,
    originalYear: "2002",
    component: lazy(() => import("./guesser/Guesser")),
    author: "Scott Brown"
  },
  {
    name: "balloonGame",
    summary: "Play a collection of balloon games in your browser.",
    createdAt: "2024-09-29",
    title: "Balloon Games",
    desc: "Balloon games originally written in Java in 2005, remade in C# with Unity in 2024",
    originalYear: "2005",
    originalLanguage: "Java",
    remakeLanguage: "C#",
    remakeFramework: "Unity",
    remakeYear: "2024",
    image: balloonGamesPreview,
    component: lazy(() => import("./balloonGames/BalloonGames")),
    author: "Scott Brown"
  },
  {
    name: "lightOn",
    summary: "Solve a grid of lights by finding the right switches.",
    createdAt: "2024-09-29",
    title: "Light On",
    desc: "A puzzle game originally written in Java with Swing in 2005",
    originalFramework: "Swing",
    originalLanguage: "Java",
    originalYear: "2005",
    image: lightOnPreview,
    component: LightOn,
    author: "Scott Brown"
  },
  {
    name: "prisonBreak",
    summary: "Explore the prison and work out your escape.",
    createdAt: "2024-09-29",
    title: "Prison Break",
    desc: "A prison escape game originally written in C++ in 2007",
    originalLanguage: "C++",
    originalYear: "2007",
    image: prisonBreakPreview,
    component: lazy(() => import("./prisonBreak/PrisonBreak")),
    author: "Scott Brown"
  },
  {
    name: "tic",
    summary: "Get three in a row in a classic game of noughts and crosses.",
    createdAt: "2024-09-29",
    title: "Noughts and Crosses",
    desc: "Classic noughts and crosses originally written in Javascript with React in 2016",
    originalFramework: "React",
    originalLanguage: "Javascript",
    originalYear: "2016",
    component: lazy(() => import("./tictactoe/Tic")),
    image: ticPreview,
    author: "Scott Brown"
  },
  {
    name: "simpleTennis",
    summary: "Keep the ball in play in this arcade tennis game.",
    createdAt: "2024-09-29",
    title: "Simple Tennis",
    desc: "A simple tennis game originally written in Javascript in 2017",
    originalLanguage: "Javascript",
    originalYear: "2017",
    image: tennisPreview,
    component: lazy(() => import("./simpleTennis/SimpleTennis")),
    author: "Scott Brown"
  },
  {
    name: "rps",
    summary: "Outguess your opponents in a live bracket tournament.",
    createdAt: "2026-03-22",
    title: "Rock Paper Scissors Tournament",
    desc: "Real-time multiplayer Rock Paper Scissors with lobby system and bracket tournaments",
    originalFramework: "React",
    originalLanguage: "Typescript",
    originalYear: "2026",
    image: rpsPreview,
    component: lazy(() => import("./rpsTournament/RpsTournament")),
    author: "Scott Brown"
  },
  {
    name: "ship-battle",
    summary:
      "Place your fleet and sink the computer’s ships before it sinks yours.",
    createdAt: "2026-04-24",
    title: "Ship Battle",
    desc: "Classic turn-based Battleship against the computer — place your fleet, then sink the bot's ships before it sinks yours. Three difficulty levels.",
    originalFramework: "React",
    originalLanguage: "Typescript",
    originalYear: "2026",
    image: shipBattlePreview,
    component: lazy(() => import("./shipBattle/ShipBattle")),
    author: "Scott Brown"
  },
  {
    name: "maze-maker",
    summary: "Find your way from start to exit in a randomly generated maze.",
    createdAt: "2026-04-24",
    title: "Maze Maker",
    desc: "Navigate a randomly generated 2D maze from start to exit",
    originalFramework: "React",
    originalLanguage: "Typescript",
    originalYear: "2026",
    image: mazeMakerPreview,
    component: lazy(() => import("./mazeMaker/MazeMaker")),
    author: "Scott Brown"
  },
  {
    name: "risky-space",
    summary:
      "Send fleets between planets and expand your territory against friends or bots.",
    createdAt: "2026-04-25",
    title: "Risky Space",
    desc: "Real-time galactic conquest RTS: drag fleets along lanes to capture planets. 2–8 players with AI bots, procedural maps.",
    originalFramework: "React + pixi.js",
    originalLanguage: "Typescript",
    originalYear: "2026",
    image: riskySpacePreview,
    component: lazy(() => import("./riskySpace/RiskySpace")),
    author: "Scott Brown"
    // The lobby views render inside the standard Page chrome (title bar);
    // MatchView escapes to a viewport-fixed full-bleed canvas itself.
  },
  {
    name: "snake",
    summary: "Eat, grow, and keep clear of the walls and your own tail.",
    createdAt: "2026-05-17",
    title: "Snake",
    desc: "Classic grid-based snake — eat food, grow, don't hit the walls or yourself",
    originalFramework: "React",
    originalLanguage: "Typescript",
    originalYear: "2026",
    image: snakePreview,
    component: lazy(() => import("./snake/Snake")),
    author: "Scott Brown"
  },
  {
    name: "sirtet",
    summary:
      "Stack falling blocks, clear lines, and keep up as the pace increases.",
    createdAt: "2026-10-04",
    title: "Sirtet",
    desc: "A Tetris-style falling-block puzzle with seven pieces, hold, landing previews, and endless levels. Play with keyboard or touch controls.",
    originalFramework: "React",
    originalLanguage: "Typescript",
    originalYear: "2026",
    image: sirtetPreview,
    component: lazy(() => import("./sirtet/Sirtet")),
    author: "Scott Brown"
  },
  {
    name: "tank-stike",
    summary:
      "Read the wind and blast rival tanks across a destructible battlefield.",
    createdAt: "2026-10-04",
    title: "Tank Stike",
    desc: "A 2D tactical artillery game with destructible terrain, wind, three weapons, and two tanks per team. Battle the computer or a friend on the same device.",
    originalFramework: "React + SVG",
    originalLanguage: "Typescript",
    originalYear: "2026",
    image: tankStikePreview,
    component: lazy(() => import("./tankStike/TankStike")),
    author: "Scott Brown"
  }
];
